import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Power, Edit2, X } from "lucide-react";

function UserForm({ role, initial, onSave, onCancel }) {
  const [f, setF] = useState(initial || {
    email: "", password: "", name: "", phone: "",
    razon_social: role === "cliente" ? "" : undefined,
    zonas: role === "repartidor" ? "" : undefined,
  });
  const submit = (e) => {
    e.preventDefault();
    const payload = { ...f, role };
    if (role === "repartidor" && typeof payload.zonas === "string") {
      payload.zonas = payload.zonas.split(",").map((s) => s.trim()).filter(Boolean);
    }
    if (initial && !payload.password) delete payload.password;
    if (initial) delete payload.email;
    onSave(payload);
  };
  return (
    <form onSubmit={submit} className="p-6 bg-zinc-900 border border-white/10 space-y-4">
      <div className="flex items-center justify-between mb-2">
        <div className="font-display font-bold text-xl">{initial ? "Editar" : "Nuevo"} {role === "cliente" ? "cliente" : "repartidor"}</div>
        <button type="button" onClick={onCancel} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {!initial && (
          <div>
            <label className="font-mono-tech text-xs uppercase tracking-widest text-zinc-400 mb-1 block">Email</label>
            <input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="w-full bg-transparent border border-white/20 focus:border-yellow-300 outline-none px-3 py-2 font-mono-tech text-sm" data-testid="user-form-email" />
          </div>
        )}
        <div>
          <label className="font-mono-tech text-xs uppercase tracking-widest text-zinc-400 mb-1 block">Contraseña {initial && "(dejar en blanco para mantener)"}</label>
          <input {...(!initial && { required: true })} type="password" value={f.password || ""} onChange={(e) => setF({ ...f, password: e.target.value })} className="w-full bg-transparent border border-white/20 focus:border-yellow-300 outline-none px-3 py-2 font-mono-tech text-sm" data-testid="user-form-password" />
        </div>
        <div>
          <label className="font-mono-tech text-xs uppercase tracking-widest text-zinc-400 mb-1 block">Nombre</label>
          <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="w-full bg-transparent border border-white/20 focus:border-yellow-300 outline-none px-3 py-2 text-sm" data-testid="user-form-name" />
        </div>
        <div>
          <label className="font-mono-tech text-xs uppercase tracking-widest text-zinc-400 mb-1 block">Teléfono</label>
          <input value={f.phone || ""} onChange={(e) => setF({ ...f, phone: e.target.value })} className="w-full bg-transparent border border-white/20 focus:border-yellow-300 outline-none px-3 py-2 text-sm" data-testid="user-form-phone" />
        </div>
        {role === "cliente" && (
          <>
            <div className="md:col-span-2">
              <label className="font-mono-tech text-xs uppercase tracking-widest text-zinc-400 mb-1 block">Razón social</label>
              <input value={f.razon_social || ""} onChange={(e) => setF({ ...f, razon_social: e.target.value })} className="w-full bg-transparent border border-white/20 focus:border-yellow-300 outline-none px-3 py-2 text-sm" data-testid="user-form-razon" />
            </div>
            <div className="md:col-span-2">
              <label className="font-mono-tech text-xs uppercase tracking-widest text-zinc-400 mb-2 block">Foto obligatoria en entrega</label>
              <select value={f.photo_required_on_delivery === null || f.photo_required_on_delivery === undefined ? "inherit" : (f.photo_required_on_delivery ? "true" : "false")}
                onChange={(e) => {
                  const v = e.target.value;
                  setF({ ...f, photo_required_on_delivery: v === "inherit" ? null : v === "true" });
                }}
                className="w-full bg-zinc-900 border border-white/20 focus:border-yellow-300 outline-none px-3 py-2 text-sm font-mono-tech"
                data-testid="user-form-photo-required">
                <option value="inherit">Usar configuración global (default)</option>
                <option value="true">Obligatoria (siempre pedir foto)</option>
                <option value="false">Opcional (permitir entregar sin foto)</option>
              </select>
              <div className="text-[10px] font-mono-tech text-zinc-500 mt-1 uppercase tracking-widest">Sobrescribe el ajuste global solo para este cliente</div>
            </div>
          </>
        )}
        {role === "repartidor" && (
          <div className="md:col-span-2">
            <label className="font-mono-tech text-xs uppercase tracking-widest text-zinc-400 mb-1 block">Zonas (separadas por coma)</label>
            <input value={Array.isArray(f.zonas) ? f.zonas.join(", ") : (f.zonas || "")} onChange={(e) => setF({ ...f, zonas: e.target.value })} placeholder="Providencia, Ñuñoa, Las Condes" className="w-full bg-transparent border border-white/20 focus:border-yellow-300 outline-none px-3 py-2 text-sm" data-testid="user-form-zonas" />
          </div>
        )}
      </div>
      <div className="flex gap-2 pt-2">
        <button type="submit" className="bg-yellow-300 text-black font-bold px-6 py-2.5 uppercase tracking-widest text-sm hover:bg-yellow-200" data-testid="user-form-submit">Guardar</button>
        <button type="button" onClick={onCancel} className="border border-white/20 hover:border-white/40 px-6 py-2.5 uppercase tracking-widest text-sm">Cancelar</button>
      </div>
    </form>
  );
}

export function AdminUsersPage({ role }) {
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const title = role === "cliente" ? "Clientes / Aliados" : "Repartidores";

  const load = () => api.get(`/users?role=${role}`).then((r) => setUsers(r.data));
  useEffect(() => { load(); }, [role]);

  const save = async (payload) => {
    try {
      if (editing) {
        await api.patch(`/users/${editing.id}`, payload);
        toast.success("Actualizado");
      } else {
        await api.post("/users", payload);
        toast.success("Creado");
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggle = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Layout>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-tech text-xs text-yellow-300 uppercase tracking-widest mb-2">/ Gestión</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">{title}</h1>
        </div>
        <button
          data-testid="new-user-btn"
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="bg-yellow-300 text-black font-bold px-5 py-3 uppercase tracking-widest text-sm hover:bg-yellow-200 inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Nuevo
        </button>
      </div>

      {showForm && (
        <div className="mb-8">
          <UserForm role={role} initial={editing} onSave={save} onCancel={() => { setShowForm(false); setEditing(null); }} />
        </div>
      )}

      <div className="border border-white/10 overflow-x-auto" data-testid="users-table">
        <table className="w-full min-w-[720px]">
          <thead className="bg-zinc-900">
            <tr className="text-left text-[10px] font-mono-tech uppercase tracking-widest text-zinc-500">
              <th className="px-4 py-3">Nombre</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Teléfono</th>
              <th className="px-4 py-3">{role === "cliente" ? "Razón social" : "Zonas"}</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {users.map((u) => (
              <tr key={u.id} className="hover:bg-zinc-900/60" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td className="px-4 py-3 font-mono-tech text-sm text-zinc-400">{u.email}</td>
                <td className="px-4 py-3 font-mono-tech text-sm text-zinc-400">{u.phone || "-"}</td>
                <td className="px-4 py-3 text-sm text-zinc-400">
                  {role === "cliente" ? (u.razon_social || "-") : (u.zonas?.join(", ") || "-")}
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 border text-xs font-mono-tech uppercase tracking-wider ${u.active ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" : "border-zinc-700 text-zinc-500"}`}>
                    {u.active ? "Activo" : "Inactivo"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 justify-end">
                    <button onClick={() => { setEditing(u); setShowForm(true); }} className="w-8 h-8 border border-white/10 hover:border-yellow-300/40 hover:text-yellow-300 flex items-center justify-center" data-testid={`edit-user-${u.id}`}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => toggle(u)} className="w-8 h-8 border border-white/10 hover:border-yellow-300/40 hover:text-yellow-300 flex items-center justify-center" data-testid={`toggle-user-${u.id}`}>
                      <Power className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500 font-mono-tech text-sm">Sin registros</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}

export const AdminClientes = () => <AdminUsersPage role="cliente" />;
export const AdminRepartidores = () => <AdminUsersPage role="repartidor" />;
